/**
 * Schreiben, ohne die Seite zu verlassen — an Kunden, Lieferanten, Interessenten.
 *
 * Ein Fenster über der Seite, wie beim Versand von Angeboten und Rechnungen.
 * Gesendet wird aus dem eigenen Postfach; die Mail steht danach sofort im
 * Verlauf des Projekts (oder am Leed). Clara kann einen Text vorschlagen — aus
 * dem Verlauf und ein paar Stichworten. Abgeschickt wird immer von Hand.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";

import { fehlerText } from "@vimaana/ui";

import { useMailKonto } from "../../hooks/useMail";
import { useMailSignatur, usePostEntwurf, usePostSchreiben, type SchreibenErgebnis } from "../../hooks/usePost";
import KiHerkunft from "../ki/KiHerkunft";
import "../../styles/post.css";

export type SchreibZiel = { email: string; wer: string };

type Props = {
  onClose: () => void;
  /** Wohin die Mail im Verlauf gehört — mindestens eines. */
  projektId?: string;
  kundeId?: string;
  anfrageId?: string;
  /** Lieferant, wenn es weder Projekt noch Kunde noch Leed gibt (Notiz am Lieferanten). */
  lieferantId?: string;
  /** Bekannte Adressen zum Antippen: Kunde, Ansprechpartner, Lieferanten des Projekts. */
  ziele: SchreibZiel[];
  an?: string;
  betreff?: string;
  /** Antwort auf diese Nachricht (connect_message.id) — hält das Gespräch zusammen. */
  antwortAuf?: string;
  titel?: string;
  /** Vorbelegte Stichworte für Claras Entwurf — z. B. der Punkt aus einer Notiz. */
  stichworte?: string;
  /** Vorbelegter Mailtext — z. B. die Materialanfrage an einen Lieferanten. */
  text?: string;
  /** Ohne Schleier, als Teil eines größeren Fensters (Anfrage an mehrere Lieferanten). */
  eingebettet?: boolean;
  /** Anfrage: diese Projekt-Materialien stehen in der Mail (Migration 175). */
  anfrage?: string[];
  /** Nachfrage zu dieser Anfrage (material_anfrage.id). */
  nachfassen?: string;
  onGesendet?: (r: SchreibenErgebnis) => void;
  /** Beschriftung des Knopfs nach dem Senden (Standard „Schließen"). */
  fertigText?: string;
  /** Steht über dem Mailtext — z. B. die Prüfliste der Anfrage. */
  oben?: ReactNode;
  /** Der vorbelegte Text stammt (teils) von Clara — Auftragsnummer für die KI-Spur. */
  entwurfId?: string;
};

const MAIL_MUSTER = /^[^@\s,;<>]+@[^@\s,;<>]+\.[A-Za-z]{2,}$/;

export default function PostSchreiben({ onClose, projektId, kundeId, anfrageId, lieferantId, ziele, an = "", betreff = "", antwortAuf, titel, stichworte: stichworteVorgabe = "", text: textVorgabe = "", eingebettet = false, anfrage, nachfassen, onGesendet, fertigText, oben, entwurfId: entwurfIdVorgabe = "" }: Props) {
  const konto = useMailKonto();
  const senden = usePostSchreiben();
  const entwurf = usePostEntwurf();
  const [empfaenger, setEmpfaenger] = useState(an);
  const [betreffText, setBetreffText] = useState(betreff);
  const [text, setText] = useState(textVorgabe);
  const [stichworte, setStichworte] = useState(stichworteVorgabe);
  const [kopie, setKopie] = useState(false);
  const [cc, setCc] = useState("");
  const sig = useMailSignatur();
  const [gesendet, setGesendet] = useState("");
  const [gesendetHinweis, setGesendetHinweis] = useState("");
  const textFeld = useRef<HTMLTextAreaElement>(null);
  /** Aus welchem Entwurf von Clara der Text stammt (KI-Spur) — geht beim Senden mit. */
  const [entwurfId, setEntwurfId] = useState(entwurfIdVorgabe);

  useEffect(() => {
    if (eingebettet) return undefined;
    const zu = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", zu);
    return () => window.removeEventListener("keydown", zu);
  }, [onClose, eingebettet]);

  const adressen = empfaenger.split(/[,;]/).map((t) => t.trim()).filter(Boolean);
  const ungueltig = adressen.filter((a) => !MAIL_MUSTER.test(a));
  const ccUngueltigLeer = cc.split(/[,;]/).map((t) => t.trim()).filter(Boolean).every((a) => MAIL_MUSTER.test(a));
  const bereit = adressen.length > 0 && ungueltig.length === 0 && ccUngueltigLeer && text.trim().length > 0 && !senden.isPending;

  const ccAdressen = cc.split(/[,;]/).map((t) => t.trim()).filter(Boolean);
  const ccUngueltig = ccAdressen.filter((a) => !MAIL_MUSTER.test(a));
  const ccDazu = (email: string) => {
    if (ccAdressen.some((a) => a.toLowerCase() === email.toLowerCase())) return;
    setCc(ccAdressen.concat(email).join(", "));
  };
  // Kopie-Vorschläge: der Partner zuerst, dann alles, was zu Kunde und Projekt hinterlegt ist.
  const ccVorschlaege = [
    ...(sig.data?.partner || []).map((p) => ({ email: p.email, wer: p.name.split(" ")[0] || p.email })),
    ...ziele.filter((z) => !adressen.some((a) => a.toLowerCase() === z.email.toLowerCase())),
  ];

  const zielDazu = (email: string) => {
    if (adressen.some((a) => a.toLowerCase() === email.toLowerCase())) return;
    setEmpfaenger(adressen.concat(email).join(", "));
  };

  const vorschlagen = () => {
    entwurf.mutate(
      {
        projekt_id: projektId, anfrage_id: projektId ? undefined : anfrageId,
        kunde_id: projektId || anfrageId ? undefined : kundeId,
        lieferant_firma_id: projektId || anfrageId || kundeId ? undefined : lieferantId,
        an: empfaenger, stichworte, betreff: betreffText,
      },
      {
        onSuccess: (e) => {
          setText(e.text);
          setEntwurfId(e.auftrag_id || "");
          if (!betreffText.trim() && e.betreff) setBetreffText(e.betreff);
          textFeld.current?.focus();
        },
      },
    );
  };

  const abschicken = () => {
    if (!bereit) return;
    senden.mutate(
      {
        an: adressen.join(", "), betreff: betreffText, text,
        projekt_id: projektId || "", kunde_id: kundeId || "", anfrage_id: anfrageId || "",
        lieferant_firma_id: lieferantId || "",
        antwort_auf: antwortAuf || null, kopie_an_mich: kopie, cc,
        ...(anfrage?.length ? { material_anfrage: anfrage } : {}),
        ...(nachfassen ? { anfrage_nachfassen: nachfassen } : {}),
        ...(entwurfId ? { entwurf_id: entwurfId } : {}),
      },
      { onSuccess: (r) => { setGesendet(r.an.join(", ")); setGesendetHinweis(r.hinweis || ""); onGesendet?.(r); } },
    );
  };

  const nichtVerbunden = konto.data && !konto.data.verbunden;

  const fenster = (
      <section className={"vg-schreib" + (eingebettet ? " ist-eingebettet" : "")} role={eingebettet ? "region" : "dialog"}
        aria-modal={eingebettet ? undefined : true} aria-label={titel || "Nachricht schreiben"}>
        <header className="vg-schreib-kopf">
          <div>
            <p className="vg-schreib-ueber">{antwortAuf ? "Antworten" : anfrage?.length ? "Anfrage" : "Schreiben"}</p>
            <h2>{titel || "Neue Nachricht"}</h2>
          </div>
          {!eingebettet && <button type="button" className="vg-schreib-zu" onClick={onClose} aria-label="Schließen">×</button>}
        </header>

        {gesendet ? (
          <div className="vg-schreib-fertig">
            <p>Gesendet an <strong>{gesendet}</strong>. Die Mail steht im Verlauf{anfrage?.length ? ", die Materialien stehen auf „angefragt“" : ""}.</p>
            {gesendetHinweis && <p className="vg-post-fehler">{gesendetHinweis}</p>}
            <button type="button" className="vg-btn vg-btn-primary" onClick={onClose}>{fertigText || "Schließen"}</button>
          </div>
        ) : nichtVerbunden ? (
          <p className="vg-schreib-hinweis">
            {konto.data?.hinweis || "Dafür muss zuerst dein eigenes Postfach verbunden sein — das geht in den Einstellungen."}
          </p>
        ) : (
          <>
            {oben}
            <label className="vg-schreib-zeile">
              <span>An</span>
              <input
                className="vg-schreib-feld" type="text" inputMode="email" autoComplete="off"
                value={empfaenger} onChange={(e) => setEmpfaenger(e.target.value)}
                placeholder="name@beispiel.de — mehrere mit Komma"
              />
            </label>
            {ziele.length > 0 && (
              <div className="vg-schreib-ziele" aria-label="Bekannte Adressen">
                {ziele.map((z) => (
                  <button
                    key={z.email} type="button" className="vg-chip-knopf"
                    disabled={adressen.some((a) => a.toLowerCase() === z.email.toLowerCase())}
                    onClick={() => zielDazu(z.email)} title={z.email}
                  >
                    + {z.wer}
                  </button>
                ))}
              </div>
            )}
            {ungueltig.length > 0 && <p className="vg-post-fehler">Keine gültige Adresse: {ungueltig.join(", ")}</p>}

            <label className="vg-schreib-zeile">
              <span>Kopie an</span>
              <input
                className="vg-schreib-feld" type="text" inputMode="email" autoComplete="off"
                value={cc} onChange={(e) => setCc(e.target.value)} placeholder="optional — antippen oder frei eingeben"
              />
            </label>
            {ccVorschlaege.length > 0 && (
              <div className="vg-schreib-ziele" aria-label="Vorschläge für die Kopie">
                {ccVorschlaege.map((z) => (
                  <button
                    key={"cc-" + z.email} type="button" className="vg-chip-knopf"
                    disabled={ccAdressen.some((a) => a.toLowerCase() === z.email.toLowerCase())}
                    onClick={() => ccDazu(z.email)} title={z.email}
                  >
                    + {z.wer}
                  </button>
                ))}
              </div>
            )}
            {ccUngueltig.length > 0 && <p className="vg-post-fehler">Keine gültige Adresse in der Kopie: {ccUngueltig.join(", ")}</p>}

            <label className="vg-schreib-zeile">
              <span>Betreff</span>
              <input className="vg-schreib-feld" type="text" value={betreffText} onChange={(e) => setBetreffText(e.target.value)} />
            </label>

            <div className="vg-schreib-clara">
              <input
                className="vg-schreib-feld" type="text" value={stichworte}
                onChange={(e) => setStichworte(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); vorschlagen(); } }}
                placeholder="Stichworte für Clara, z. B. „7.10. ab 9 Uhr, zu zweit, ca. 3 Stunden“"
                aria-label="Stichworte für Clara"
              />
              <button type="button" className="vg-btn" disabled={entwurf.isPending} onClick={vorschlagen}>
                {entwurf.isPending ? "Clara schreibt …" : text.trim() ? "Clara: neu vorschlagen" : "Clara: Text vorschlagen"}
              </button>
            </div>
            {entwurf.isPending && <p className="vg-schreib-hinweis">Clara liest den Verlauf und schreibt — das dauert bis zu einer Minute. Du kannst solange selbst tippen.</p>}
            {entwurf.error && <p className="vg-post-fehler">{fehlerText(entwurf.error)}</p>}
            {entwurfId && !entwurf.isPending && <KiHerkunft auftragId={entwurfId} knopf="Wie entstand der Entwurf?" />}

            <textarea
              ref={textFeld} className="vg-schreib-feld vg-schreib-text" rows={12} value={text}
              onChange={(e) => setText(e.target.value)} placeholder="Deine Nachricht" aria-label="Nachricht"
            />

            {sig.data?.signatur ? (
              <pre className="vg-schreib-signatur" aria-label="Signatur, wird angehängt">{sig.data.signatur}</pre>
            ) : (
              <p className="vg-schreib-hinweis">Noch keine Signatur hinterlegt — das geht unter Einstellungen → Eigenes Postfach.</p>
            )}
            {senden.error && <p className="vg-post-fehler">{fehlerText(senden.error)}</p>}
            <footer className="vg-schreib-fuss">
              <label className="vg-schreib-haken">
                <input type="checkbox" checked={kopie} onChange={(e) => setKopie(e.target.checked)} /> Kopie an mich
              </label>
              <span className="vg-schreib-von">{konto.data?.email ? `von ${konto.data.email}` : ""}</span>
              {!eingebettet && <button type="button" className="vg-btn vg-btn-ghost" onClick={onClose}>Abbrechen</button>}
              <button type="button" className="vg-btn vg-btn-primary" disabled={!bereit} onClick={abschicken}>
                {senden.isPending ? "Sende …" : "Senden"}
              </button>
            </footer>
          </>
        )}
      </section>
  );
  if (eingebettet) return fenster;
  return (
    <div className="vg-schreib-schleier" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {fenster}
    </div>
  );
}
